import React, { useState, useEffect } from 'react';
import { useSession } from '../Utils/SessionContext';
import { API_ENDPOINTS } from '../Utils/ApiConfig';

const DuplicateRoleItem = ({ isOpen, onClose, roleData, onConfirm, prompt_id = null }) => {
  const { user } = useSession();
  const [mainCategory, setMainCategory] = useState([]);
  const [subCategory, setSubCategory] = useState([]);
  const [newMainCategory, setNewMainCategory] = useState('');
  const [newSubCategory, setNewSubCategory] = useState('');
  const [isCloning, setIsCloning] = useState(false);

  // Mode detection
  const isPromptMode = prompt_id !== null;
  const modalTitle = isPromptMode ? 'Duplicate Prompt' : 'Duplicate Role';
  const modalDescription = isPromptMode 
    ? 'Modify the categories for the duplicated prompt. The prompt content will remain the same.'
    : 'Modify the categories for the duplicated role. The content will remain the same.';

  // Initialize categories when modal opens
  useEffect(() => {
    if (isOpen && roleData) {
      setMainCategory([...roleData.mainCategory]);
      setSubCategory([...roleData.subCategory]);
    }
  }, [isOpen, roleData]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setNewMainCategory('');
      setNewSubCategory('');
    }
  }, [isOpen]);

  const handleAddMainCategory = () => {
    if (newMainCategory.trim() && !mainCategory.includes(newMainCategory.trim())) {
      setMainCategory(prev => [...prev, newMainCategory.trim()]);
      setNewMainCategory('');
    }
  };

  const handleRemoveMainCategory = (index) => {
    setMainCategory(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddSubCategory = () => {
    if (newSubCategory.trim() && !subCategory.includes(newSubCategory.trim())) {
      setSubCategory(prev => [...prev, newSubCategory.trim()]);
      setNewSubCategory('');
    }
  };

  const handleRemoveSubCategory = (index) => {
    setSubCategory(prev => prev.filter((_, i) => i !== index));
  };

  const handleConfirm = async () => {
    if (mainCategory.length === 0) {
      alert('Please add at least one main category');
      return;
    }
    if (subCategory.length === 0) {
      alert('Please add at least one subcategory');
      return;
    }

    if (!user?.access_token) {
      alert('Authentication required. Please log in again.');
      return;
    }

    try {
      setIsCloning(true);

      let requestBody;
      let endpoint;

      if (isPromptMode) {
        // Single prompt duplication mode
        requestBody = {
          prompt_id: prompt_id,
          newCategories: {
            mainCategory: [...mainCategory],
            subCategory: [...subCategory]
          }
        };
        endpoint = API_ENDPOINTS.CLONE_PROMPT_BY_ID;
        console.log('Cloning single prompt:', requestBody);
      } else {
        // Role duplication mode (existing logic)
        requestBody = {
          oldCategories: {
            mainCategory: roleData.mainCategory,
            subCategory: roleData.subCategory
          },
          newCategories: {
            mainCategory: [...mainCategory],
            subCategory: [...subCategory]
          }
        };
        endpoint = API_ENDPOINTS.CLONE_PROMPTS;
        console.log('Cloning prompts with categories:', requestBody);
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${user.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`Server responded with status: ${response.status}`);
      }

      const data = await response.json();
      console.log(`${isPromptMode ? 'Prompt' : 'Prompts'} cloned successfully:`, data);

      // Create the duplicated object for local state update
      const duplicatedItem = {
        ...roleData,
        mainCategory: [...mainCategory],
        subCategory: [...subCategory]
      };

      onConfirm(duplicatedItem);
      onClose();

    } catch (err) {
      console.error(`Error cloning ${isPromptMode ? 'prompt' : 'prompts'}:`, err);
      alert(`Failed to clone ${isPromptMode ? 'prompt' : 'prompts'}: ${err.message}`);
    } finally {
      setIsCloning(false);
    }
  };

  const handleKeyPress = (e, type) => {
    if (e.key === 'Enter') {
      if (type === 'main') {
        handleAddMainCategory();
      } else {
        handleAddSubCategory();
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-800">{modalTitle}</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <p className="text-sm text-gray-600 mt-2">
            {modalDescription}
          </p>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Main Categories */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Main Categories
            </label>
            
            {/* Add new main category */}
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={newMainCategory}
                onChange={(e) => setNewMainCategory(e.target.value)}
                onKeyPress={(e) => handleKeyPress(e, 'main')}
                placeholder="Add new main category"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button
                onClick={handleAddMainCategory}
                className="px-4 py-2 bg-blue-500 text-white text-sm rounded-md hover:bg-blue-600 transition-colors"
              >
                Add
              </button>
            </div>

            {/* Current main categories */}
            <div className="space-y-2">
              {mainCategory.map((cat, index) => (
                <div key={index} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-md">
                  <span className="text-sm text-gray-700">{cat}</span>
                  <button
                    onClick={() => handleRemoveMainCategory(index)}
                    className="text-red-500 hover:text-red-700 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
              {mainCategory.length === 0 && (
                <p className="text-sm text-gray-500 italic">No main categories added</p>
              )}
            </div>
          </div>

          {/* Subcategories */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Subcategories
            </label>
            
            {/* Add new subcategory */}
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={newSubCategory}
                onChange={(e) => setNewSubCategory(e.target.value)}
                onKeyPress={(e) => handleKeyPress(e, 'sub')}
                placeholder="Add new subcategory"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button
                onClick={handleAddSubCategory}
                className="px-4 py-2 bg-blue-500 text-white text-sm rounded-md hover:bg-blue-600 transition-colors"
              >
                Add
              </button>
            </div>

            {/* Current subcategories */}
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {subCategory.map((sub, index) => (
                <div key={index} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-md">
                  <span className="text-sm text-gray-700">{sub}</span>
                  <button
                    onClick={() => handleRemoveSubCategory(index)}
                    className="text-red-500 hover:text-red-700 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
              {subCategory.length === 0 && (
                <p className="text-sm text-gray-500 italic">No subcategories added</p>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={isCloning}
            className={`px-4 py-2 rounded-md transition-colors flex items-center gap-2 ${
              isCloning
                ? 'bg-blue-400 text-white cursor-not-allowed'
                : 'bg-blue-500 text-white hover:bg-blue-600'
            }`}
          >
            {isCloning && (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            )}
            {isCloning ? 'Cloning...' : `Duplicate ${isPromptMode ? 'Prompt' : 'Role'}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DuplicateRoleItem;
